import _ from "lodash";
import React from "react";
import classes from "./LearningStandards.scss";
import classNames from "classnames";
import { colors } from "Constants";
import { getHTMLLabel } from "NodeEditor/Utils";
import { Checkbox, IconButton, Tooltip } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import {
  WarningTriangleOutlined,
  RightArrowOutlined,
  LeftArrowOutlined,
  DotsSixVerticalOutlined,
  AddCircleOutlined,
  InformationOutlined,
} from "@toddle-edu/ds-icons";
import LinkWithTooltip from "UIComponents/LinkWithTooltip";
import { getStyleStrippedText, onEnterOrSpaceKeyDown } from "Utils";
import ACLStore from "lib/aclStore";

const CELL_NAME = "label";

const styles = {
  disableStyle: { fill: colors.neutral500, cursor: "not-allowed" },
};

const getStandardsRowStyleMemoize = _.memoize(
  params => getStandardsRowStyle(params),
  params => JSON.stringify(params)
);

const getStandardsRowStyle = ({
  depth,
  showSelectionBox,
  mode,
  currentEditorConfig,
}) => {
  /**
   * Condition  for showSelectionBox is added for Unit Plan SNS editor
   * Mode === Edit condition will be applied for Admin Portal SNS editor
   * _.get(currentEditorConfig, "showSelectionBox", false) condtion will be applied for Admin Portal SNS view mode
   **/
  const defaultMargin =
    showSelectionBox ||
    mode === "edit" ||
    !_.get(currentEditorConfig, "showSelectionBox", false)
      ? 0
      : 30;
  if (depth < 5) return { marginLeft: `${(depth - 1) * 30 + defaultMargin}px` };
  else
    return {
      marginLeft: (depth - 1) * 30 + defaultMargin + "px",
      color: colors.textSubtle,
    };
};

const shouldShowDescriptionIcon = ({ currentEditorConfig }) => {
  if (_.find(currentEditorConfig.columns, { id: "SUBTEXT" })) {
    return false;
  }
  return true;
};

const shouldShowDescriptionIconMemoize = _.memoize(
  params => shouldShowDescriptionIcon(params),
  params => JSON.stringify(params)
);

const LearningStandards = props => {
  const {
    node,
    mode,
    dragHandleProps,
    onContentEdit,
    onLeftIndent,
    onRightIndent,
    onAddRow,
    placeholdersText,
    filters: { searchText },
    onCheckboxClick,
    isActionDisabled,
    isError,
    currentEditorConfig,
    removeError,
    onBulkPasteRows,
    indexOfNode,
    isCoachmarksRunning,
    updateDraftNode,
    showSelectionBox,
    onCheckBoxClick,
    isNodeSelected,
    dragAndDropEnabled,
    testId,
  } = props;

  const { t } = useI18n(["common", "scopeAndSequence"]);
  const depth = node.depth;
  const isLabelEmpty = _.isEmpty(node.label);

  const isSearchFilter = !_.isEmpty(searchText);
  const actionDisableStyle = isActionDisabled ? styles.disableStyle : {};
  const iconClasses = classNames({ [classes.iconDisabled]: isActionDisabled });
  const addIconClasses = classNames({
    [classes.iconDisabled]: isSearchFilter,
  });

  const textStyles = classNames({
    ["heading-5"]: depth == 1,
    ["heading-6"]: depth == 2,
    ["text-label-3"]: depth == 3,
    ["text-body-2"]: depth > 3,
    [classes.textContainer]: true,
    [classes.selectionBox]: showSelectionBox,
  });

  const handleContentEdit = event => {
    event.stopPropagation();
    const updatedLabel = getStyleStrippedText({
      text: event.currentTarget.innerHTML,
      type: "SNS_EDITOR",
    });
    // const updatedLabel = event.currentTarget.textContent;
    const prevLabel = node.label;

    if (updatedLabel !== prevLabel || _.isEmpty(updatedLabel)) {
      const params = {
        label: updatedLabel,
      };
      onContentEdit({ params });
    }
  };
  //dynamic level style
  const standardsRowStyle = getStandardsRowStyleMemoize({
    depth,
    showSelectionBox,
    mode,
    currentEditorConfig,
  });

  const showDescriptionIcon = shouldShowDescriptionIconMemoize({
    currentEditorConfig,
  });

  //left right indentation functions
  const handleLeftIndent = () => {
    if (!isActionDisabled) {
      onLeftIndent();
    }
  };

  const handleAddRow = () => {
    if (!isSearchFilter) onAddRow();
  };

  const handleRightIndent = () => {
    if (!isActionDisabled) {
      onRightIndent();
    }
  };

  const newLabel = getHTMLLabel({
    searchText,
    label: node.label,
    style: { color: colors.blue400 },
    type: showDescriptionIcon ? "SNS_EDITOR" : null,
  });

  const isChecked = _.get(node, "isChecked", false);

  const handleCheckboxClick = () => {
    onCheckboxClick({ prevIsChecked: isChecked });
  };

  const handleOnPaste = event => {
    const pastedData = event.clipboardData.getData("Text");
    const itemsList = pastedData.split(/\r?\n/); // regex spitting the excel sheet data into a list;
    const itemsListLength = _.size(itemsList);
    if (itemsListLength > 1) {
      // the list will be pasted in row format
      event.preventDefault();
      onBulkPasteRows({
        pastedData,
        cellName: CELL_NAME,
        isNewRow: true,
        itemsList,
      });
    }
  };

  const dragDropContainerStyle = classNames({
    [classes.dragDropIcon]: true,
    [classes.dragDropIconContainer]: !isActionDisabled,
    [classes.dragDropIconContainerDisabled]: isActionDisabled,
  });

  //if error is present, generate the error text
  //get the error text to display in tooltip of warning icon
  const getErrorText = () => {
    const errorCheckFields = currentEditorConfig.errorCheckFields;
    const errorCheckFieldLabels = currentEditorConfig.errorCheckFieldLabels;
    const missingFieldLabels = [];
    for (const field of errorCheckFields) {
      if (_.isEmpty(node[field])) {
        missingFieldLabels.push(t(errorCheckFieldLabels[field]));
      }
    }
    const errorText = missingFieldLabels.join(", ");
    return errorText;
  };
  const errorText = isError ? getErrorText() : "";
  const addIconTooltipText = isSearchFilter
    ? t("scopeAndSequence:disable_on_search")
    : t("common:add_with_label", { label: placeholdersText[depth] });
  //this function removes the error state if it exists for a row
  const resolveError = _.debounce(
    () => {
      removeError();
    },
    7000,
    { leading: true }
  );

  const handleOnInput = event => {
    const updatedLabel = getStyleStrippedText({
      text: event.currentTarget.innerHTML,
      type: "SNS_EDITOR",
    });
    // const updatedLabel = event.currentTarget.textContent;
    if (!_.isEmpty(updatedLabel) && isError) {
      resolveError();
    }
    updateDraftNode({ cellName: CELL_NAME, labelValue: updatedLabel });
  };

  const onCheckboxChange = event => {
    const shouldSelectRange = event.shiftKey;
    onCheckBoxClick({ id: node.id, shouldSelectRange });
  };
  const handleAddRowKeyDown = e => {
    onEnterOrSpaceKeyDown(e, () => {
      e.stopPropagation();
      handleAddRow();
    });
  };

  return mode === "view" ? (
    showSelectionBox ? (
      <div
        className={textStyles}
        onClick={onCheckboxChange}
        style={standardsRowStyle}
        data-test-id={`${testId}-${node.id}`}
      >
        <Checkbox
          isChecked={isNodeSelected}
          size="xx-small"
          testId={`${testId}-${node.id}`}
        />
        <div>
          <span
            className={classes.rowTextContainer}
            dangerouslySetInnerHTML={{
              __html: isLabelEmpty ? "--" : newLabel,
            }}
          />
          {ACLStore.can("FeatureFlag:EnableDescriptionInSNS") &&
            node.subText &&
            showDescriptionIcon && (
              <span style={{ marginLeft: "4px" }}>
                <Tooltip
                  tooltip={node.subText}
                  overlayInnerStyle={{ maxWidth: "344px" }}
                >
                  <InformationOutlined
                    size="xxx-small"
                    variant="subtle"
                    style={{ position: "relative", top: "2px" }}
                  />
                </Tooltip>
              </span>
            )}
        </div>
      </div>
    ) : (
      <div
        className={textStyles}
        style={{
          ...standardsRowStyle,
        }}
      >
        <span
          className={classes.rowTextContainer}
          dangerouslySetInnerHTML={{
            __html: isLabelEmpty ? "--" : newLabel,
          }}
        />
        {ACLStore.can("FeatureFlag:EnableDescriptionInSNS") &&
          node.subText &&
          showDescriptionIcon && (
            <span style={{ marginLeft: "4px" }}>
              <Tooltip
                tooltip={node.subText}
                overlayInnerStyle={{ maxWidth: "344px" }}
              >
                <InformationOutlined
                  size="xxx-small"
                  variant="subtle"
                  style={{ position: "relative", top: "2px" }}
                />
              </Tooltip>
            </span>
          )}
      </div>
    )
  ) : (
    <div className={classes.container}>
      <div className={classes.actionButtonContainer}>
        {!isError && (
          <div
            className={classes.addIconContainer}
            style={
              isCoachmarksRunning && indexOfNode == 1
                ? { visibility: "visible" }
                : {}
            }
            tabIndex={!isError ? 0 : -1}
            onKeyDown={handleAddRowKeyDown}
          >
            <div
              className={classes.addIcon}
              onClick={handleAddRow}
              style={
                isCoachmarksRunning && indexOfNode == 1
                  ? { visibility: "visible" }
                  : actionDisableStyle
              }
              id={
                indexOfNode == 1
                  ? "COACHMARKS_SCOPE_SEQUENCE_EDITOR_ADD_STANDARDS"
                  : ""
              }
            >
              <LinkWithTooltip
                tooltip={addIconTooltipText}
                placement={"top"}
                trigger={["hover", "focus"]}
              >
                <span
                  className={classes.addIconTooltip}
                  tabIndex={!isError ? 0 : -1}
                  onKeyDown={handleAddRowKeyDown}
                >
                  <AddCircleOutlined
                    className={addIconClasses}
                    size={"xx-small"}
                  />
                </span>
              </LinkWithTooltip>
            </div>
          </div>
        )}
        {isError && (
          <div className={classes.warningIconContainer}>
            <div className={classes.warningIcon}>
              <LinkWithTooltip tooltip={errorText} placement={"top"}>
                <WarningTriangleOutlined
                  width={16}
                  height={16}
                  variant="critical"
                />
              </LinkWithTooltip>
            </div>
          </div>
        )}

        <div
          className={classes.checkboxContainer}
          id={
            indexOfNode == 1
              ? "COACHMARKS_SCOPE_SEQUENCE_EDITOR_BULK_ACTIONS"
              : ""
          }
        >
          <Checkbox
            isChecked={isChecked}
            onChange={handleCheckboxClick}
            size="xx-small"
          />
        </div>
        <div
          className={classes.arrowsContainer}
          id={
            indexOfNode == 1
              ? "COACHMARKS_SCOPE_SEQUENCE_EDITOR_HIERARCHY_ARROWS"
              : ""
          }
        >
          <IconButton
            dsVersion="2.0"
            variant="neutral"
            type="fill"
            size="small"
            onClick={handleLeftIndent}
            icon={<LeftArrowOutlined className={iconClasses} />}
          />
          <IconButton
            dsVersion="2.0"
            variant="neutral"
            type="fill"
            size="small"
            onClick={handleRightIndent}
            icon={<RightArrowOutlined className={iconClasses} />}
          />
        </div>

        {dragAndDropEnabled && (
          <div
            {...dragHandleProps}
            className={dragDropContainerStyle}
            style={
              isCoachmarksRunning && indexOfNode == 1
                ? { visibility: "visible" }
                : actionDisableStyle
            }
            id={
              indexOfNode == 1
                ? "COACHMARKS_SCOPE_SEQUENCE_EDITOR_REARRANGE_STANDARDS"
                : ""
            }
          >
            <DotsSixVerticalOutlined className={iconClasses} size={"x-small"} />
          </div>
        )}
      </div>
      <div
        className={textStyles}
        contentEditable={true}
        onBlur={handleContentEdit}
        onInput={handleOnInput}
        suppressContentEditableWarning={true}
        style={standardsRowStyle}
        placeholder={
          isLabelEmpty
            ? t("common:enter_with_label", {
                label: placeholdersText[depth],
              })
            : ""
        }
        dangerouslySetInnerHTML={{
          __html: isLabelEmpty ? "" : newLabel,
        }}
        onPasteCapture={handleOnPaste}
      />
    </div>
  );
};

const checkProps = (prevProps, nextProps) => {
  const prevPropsNode = prevProps.node;
  const nextPropsNode = nextProps.node;

  const prevPropsLabel = prevPropsNode.label;
  const nextPropsLabel = nextPropsNode.label;

  const prevPropsDepth = prevPropsNode.depth;
  const nextPropsDepth = nextPropsNode.depth;

  const prevPropsFilters = prevProps.filters;
  const nextPropsFilters = nextProps.filters;

  const prevPropsIsChecked = _.get(prevPropsNode, "isChecked", false);
  const nextPropsIsChecked = _.get(nextPropsNode, "isChecked", false);

  const prevPropsOnBulkPasteRows = prevProps.onBulkPasteRows;
  const nextPropsOnBulkPasteRows = nextProps.onBulkPasteRows;

  return (
    prevProps.placeholdersText === nextProps.placeholdersText &&
    prevPropsLabel === nextPropsLabel &&
    prevProps.mode === nextProps.mode &&
    prevPropsDepth === nextPropsDepth &&
    prevProps.onContentEdit === nextProps.onContentEdit &&
    prevProps.onLeftIndent === nextProps.onLeftIndent &&
    prevProps.onRightIndent === nextProps.onRightIndent &&
    prevProps.onAddRow === nextProps.onAddRow &&
    prevProps.removeError === nextProps.removeError &&
    prevPropsFilters === nextPropsFilters &&
    prevProps.onCheckboxClick === nextProps.onCheckboxClick &&
    prevPropsIsChecked === nextPropsIsChecked &&
    prevPropsOnBulkPasteRows === nextPropsOnBulkPasteRows &&
    prevProps.indexOfNode === nextProps.indexOfNode &&
    prevProps.isCoachmarksRunning === nextProps.isCoachmarksRunning &&
    prevProps.updateDraftNode === nextProps.updateDraftNode
  );
};

export default React.memo(LearningStandards, checkProps);
